import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { parseLocaleNum } from "../lib/locale";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, X, Scissors } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = { jenis_kain_id: "", nama_kain_snapshot: "", jumlah_roll: 0, yard_per_roll: 0, harga_per_yard: 0 };
const emptyForm = { spk_id: "", tanggal: today(), items: [{ ...emptyItem }], catatan: "" };

export default function BonKainPage() {
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [spkList, setSpkList] = useState([]);
    const [kainList, setKainList] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bon-kain", { params: q ? { q } : {} });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            const [s, k, v] = await Promise.all([api.get("/spk", { params: { status: "aktif" } }), api.get("/master/kain"), api.get("/vendors")]);
            setSpkList(s.data); setKainList(k.data); setVendors(v.data);
        })();
    }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const spkMap = useMemo(() => Object.fromEntries(spkList.map((s) => [s.id, s])), [spkList]);
    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => { setEditing(null); setForm({ ...emptyForm, items: [{ ...emptyItem }] }); setDialogOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            spk_id: row.spk_id, tanggal: row.tanggal,
            items: row.items.map((i) => ({ jenis_kain_id: i.jenis_kain_id, nama_kain_snapshot: i.nama_kain_snapshot, jumlah_roll: i.jumlah_roll, yard_per_roll: i.yard_per_roll, harga_per_yard: i.harga_per_yard })),
            catatan: row.catatan || "",
        });
        setDialogOpen(true);
    };

    const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
    const pickKain = (idx, kain_id) => {
        const k = kainList.find((x) => x.id === kain_id);
        setItem(idx, { jenis_kain_id: kain_id, nama_kain_snapshot: k?.nama_kain || "", harga_per_yard: k?.harga_default_per_yard || 0 });
    };

    const itemTotals = form.items.map((i) => {
        const totalYard = parseLocaleNum(i.jumlah_roll || 0) * parseLocaleNum(i.yard_per_roll || 0);
        const subtotal = totalYard * parseLocaleNum(i.harga_per_yard || 0);
        return { totalYard, subtotal };
    });
    const totalNilai = itemTotals.reduce((s, x) => s + x.subtotal, 0);
    const totalYardAll = itemTotals.reduce((s, x) => s + x.totalYard, 0);

    const save = async () => {
        if (!form.spk_id) return toast.error("SPK wajib dipilih");
        if (form.items.length === 0) return toast.error("Minimal 1 item kain");
        for (const it of form.items) {
            if (!it.jenis_kain_id) return toast.error("Pilih jenis kain pada setiap item");
            if (parseLocaleNum(it.jumlah_roll) <= 0) return toast.error("Jumlah roll harus > 0");
            if (parseLocaleNum(it.yard_per_roll) <= 0) return toast.error("Yard per roll harus > 0");
            if (parseLocaleNum(it.harga_per_yard) < 0) return toast.error("Harga per yard tidak boleh negatif");
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                items: form.items.map((i) => ({
                    jenis_kain_id: i.jenis_kain_id,
                    nama_kain_snapshot: i.nama_kain_snapshot,
                    jumlah_roll: parseFloat(i.jumlah_roll),
                    yard_per_roll: parseFloat(i.yard_per_roll),
                    harga_per_yard: parseFloat(i.harga_per_yard),
                })),
            };
            if (editing) { await api.put(`/bon-kain/${editing.id}`, payload); toast.success("Bon Kain diperbarui"); }
            else { await api.post("/bon-kain", payload); toast.success("Bon Kain ditambahkan"); }
            setDialogOpen(false); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try { await api.delete(`/bon-kain/${confirmDelete.id}`); toast.success("Dipindahkan ke Riwayat Terhapus. Buka Riwayat untuk pulihkan."); setConfirmDelete(null); load(); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="bon-kain-page">
            <PageHeader
                title="Bon Kain"
                description="Pencatatan kain keluar ke vendor untuk pengerjaan SPK"
                actions={canWrite() && (
                    <Button onClick={openCreate} data-testid="bon-kain-add-button-top" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Tambah Bon Kain
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <Input placeholder="Cari nomor bon..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="bon-kain-search-input" className="pl-9 rounded-lg h-10" />
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={Scissors} title="Belum ada bon kain" description="Belum ada bon kain tercatat." testid="bon-kain-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const totalYard = row.items?.reduce((s, i) => s + parseLocaleNum(i.total_yard || 0), 0) || 0;
                    return (
                        <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`bon-kain-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_bon}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>SPK: {row.nomor_spk_snapshot || "-"}</div>
                                </div>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal)}</div>
                            </div>
                            <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div className="text-xs"><span style={{ color: "var(--text-muted)" }}>{totalYard.toLocaleString("id-ID", {maximumFractionDigits: 2})} yd · </span><span className="font-semibold num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.total_nilai || 0)}</span></div>
                                <div className="flex items-center gap-1">
                                    {canWrite() && <button onClick={() => openEdit(row)} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                    {canDelete() && <button onClick={() => setConfirmDelete(row)} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="bon-kain-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Nomor Bon</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">SPK</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Total Yard</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Total Nilai</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase text-slate-500 w-28">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7} className="py-14 text-center"><div className="flex flex-col items-center gap-2 text-slate-400"><Inbox className="w-8 h-8" /><div className="text-sm">Belum ada bon kain</div></div></TableCell></TableRow>
                        ) : pageItems.map((row) => (
                            <TableRow key={row.id} data-testid={`bon-kain-row-${row.id}`} className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors">
                                <TableCell className="font-semibold text-teal-700">{row.nomor_bon}</TableCell>
                                <TableCell className="text-sm text-slate-700">{row.nomor_spk_snapshot || "-"}</TableCell>
                                <TableCell className="text-sm text-slate-700">{vendorMap[row.vendor_id] || "-"}</TableCell>
                                <TableCell className="text-sm text-slate-600">{formatTanggal(row.tanggal)}</TableCell>
                                <TableCell className="text-sm text-slate-800 text-right num-tabular">{row.items?.reduce((s, i) => s + parseLocaleNum(i.total_yard || 0), 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-sm font-semibold text-slate-900 text-right num-tabular">{formatRupiah(row.total_nilai || 0)}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {canWrite() && <button onClick={() => openEdit(row)} data-testid={`bon-kain-edit-btn-${row.id}`} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700"><Pencil className="w-4 h-4" /></button>}
                                        {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`bon-kain-delete-btn-${row.id}`} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                        <div className="text-xs text-slate-500">Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs text-slate-600 px-2">Hal. {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="bon-kain-modal-dialog" className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Bon Kain" : "Tambah Bon Kain"}</DialogTitle>
                        <DialogDescription>Kain keluar dari gudang ke vendor untuk pengerjaan SPK. Total yard & subtotal auto-hitung.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>SPK <span className="text-rose-600">*</span></Label>
                                <Select value={form.spk_id} onValueChange={(v) => setForm({ ...form, spk_id: v })} disabled={!!editing}>
                                    <SelectTrigger data-testid="bon-kain-form-spk" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih SPK aktif" /></SelectTrigger>
                                    <SelectContent>
                                        {spkList.map((s) => <SelectItem key={s.id} value={s.id}>{s.nomor_spk} · {vendorMap[s.vendor_id] || "-"}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {form.spk_id && spkMap[form.spk_id] && (
                                    <div className="text-xs text-slate-500 mt-1">Vendor: {vendorMap[spkMap[form.spk_id].vendor_id]}</div>
                                )}
                            </div>
                            <div>
                                <Label>Tanggal <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Item Kain <span className="text-rose-600">*</span></Label>
                                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="bon-kain-form-add-item" className="rounded-lg h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Kain</Button>
                            </div>
                            <div className="space-y-2">
                                {form.items.map((it, idx) => (
                                    <div key={idx} data-testid={`bon-kain-form-item-${idx}`} className="p-3 rounded-xl bg-slate-50/70 border border-slate-200/60">
                                        <div className="grid grid-cols-12 gap-2">
                                            <div className="col-span-12 sm:col-span-4">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Jenis Kain</div>
                                                <Select value={it.jenis_kain_id} onValueChange={(v) => pickKain(idx, v)}>
                                                    <SelectTrigger className="rounded-lg h-9 bg-white"><SelectValue placeholder="Pilih kain" /></SelectTrigger>
                                                    <SelectContent>
                                                        {kainList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama_kain}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="col-span-4 sm:col-span-2">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Roll</div>
                                                <Input type="text" inputMode="decimal" value={it.jumlah_roll} onChange={(e) => setItem(idx, { jumlah_roll: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-4 sm:col-span-2">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Yard/Roll</div>
                                                <Input type="text" inputMode="decimal" value={it.yard_per_roll} onChange={(e) => setItem(idx, { yard_per_roll: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-4 sm:col-span-3">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Harga/Yard</div>
                                                <Input type="text" inputMode="decimal" value={it.harga_per_yard} onChange={(e) => setItem(idx, { harga_per_yard: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-12 sm:col-span-1 flex sm:justify-center sm:items-end">
                                                {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 p-2 rounded-lg h-9"><X className="w-4 h-4" /></button>}
                                            </div>
                                        </div>
                                        <div className="flex justify-between text-xs text-slate-500 mt-2 num-tabular">
                                            <span>Total yard: <span className="font-semibold text-slate-800">{itemTotals[idx].totalYard.toLocaleString("id-ID", { maximumFractionDigits: 2 })}</span></span>
                                            <span>Subtotal: <span className="font-semibold text-slate-800">{formatRupiah(itemTotals[idx].subtotal)}</span></span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex justify-end gap-6 text-sm">
                                <div className="text-slate-600">Total Yard: <span className="font-bold num-tabular">{totalYardAll.toLocaleString("id-ID", { maximumFractionDigits: 2 })}</span></div>
                                <div className="text-slate-600">Total Nilai: <span className="font-bold text-teal-700 num-tabular">{formatRupiah(totalNilai)}</span></div>
                            </div>
                        </div>

                        <div>
                            <Label>Catatan</Label>
                            <Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="bon-kain-modal-save-button" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader><AlertDialogTitle>Hapus Bon Kain (ke Riwayat)</AlertDialogTitle><AlertDialogDescription><span className="font-semibold">{confirmDelete?.nomor_bon}</span> akan dipindahkan ke <b>Riwayat Terhapus</b> selama 30 hari dan bisa dipulihkan kapan saja. Lanjut?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">{deleting ? "..." : "Hapus (ke Riwayat)"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
